import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, Input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { HotelFiltersService } from '@app/services/hotelfilters.service';

interface Item {
    name: string;
    function: string;
    index?: number
}

@Component({
    selector: 'clever-selectedfilters',
    templateUrl: './selectedfilters.component.html',
    styleUrls: ['./selectedfilters.component.scss'],
    standalone: false
})
export class SelectedfiltersComponent implements OnInit {
    @Input() mode: string;
    @Input() type: string;
    @Input() isDisabled: boolean = false;
    isBrowser = signal(false);
    list: Item[] = [];
    minPricePerPerson: string;
    maxPricePerPerson: number;
    filter: ParamFilters;
    destroyRef = inject(DestroyRef);
    constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        protected hotelService: HotelFiltersService,
        protected cdr: ChangeDetectorRef,
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
    }

    ngOnInit(): void {
        if (this.isBrowser()) {
            this.initFilterSub();
        }
    }

    initFilterSub(): void {
        this.hotelService.getfilters$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((filter: ParamFilters) => {
            if (filter) {
                this.checkFilters(filter);
            }
        });
    }

    checkFilters(filter): void {
        this.list = [];
        this.cdr.detectChanges();
        this.filter = JSON.parse(JSON.stringify(filter));
        this.checkPrc();
        this.checkInclusiveListServiceOffer();
        if (this.filter.productType !== 'hotelonly') {
            this.checkflightCarrierList();
            this.checktransferlist();
            this.checkarrivalAirportList();
            this.checkdeparturAirportList();

            this.checkreturnTime();
            this.checkdepartureTime();
            this.checkdirektflug();
            this.checkTravelClass();
        }

        if (this.mode !== 'offers' && this.mode !== 'flight' && this.mode !== 'oneway') {
            this.checkCategory();
            this.checkkeywordList();
            this.checkRecommendation();
            this.checkRecommendationCount();
            this.checkhotelChainList();
        }
        this.checkMeerblibk();
        this.checkMeerseite();
        this.checklocationCode();
        this.checkboardTypeList();
        this.checktarifclass();
        this.checkbookingclass();
        this.checkinclusiveList();
        this.checkFacilityList();
        this.checkroomTypeList();
        this.checkroomIdList();
        this.checkserviceCodeList();
        this.checktourOperatorList();
        this.cdr.detectChanges();
        this.hotelService.setExtraCount(this.list.length);
        this.cdr.detectChanges();
    }

    checkPrc(): void {
        if (this.filter?.minPricePerPerson > 0) {
            this.list.push({
                name: 'min ' + this.filter?.minPricePerPerson + ' € p.P.',
                function: 'removeMinPrc'
            });
            this.cdr.detectChanges();
        }
        if (this.filter?.maxPricePerPerson > 0) {
            this.list.push({
                name: 'max ' + this.filter?.maxPricePerPerson + ' € p.P.',
                function: 'removeMaxPrc'
            });
            this.cdr.detectChanges();
        }
    }

    async removeMinPrc() {
        this.hotelService.price({
            min: null,
            max: this.filter?.maxPricePerPerson
        }, true);
    }

    async removeMaxPrc() {
        this.hotelService.price({
            min: this.filter?.minPricePerPerson,
            max: null
        }, true);
    }

    checkInclusiveListServiceOffer(): void {
        if (this.filter?.inclusiveListServiceOffer?.length > 0) {
            for (let i = 0; i < this.filter?.inclusiveListServiceOffer.length; i++) {
                this.list.push({
                    name: this.filter?.inclusiveListServiceOffer[i].name,
                    function: 'removeInclusiveListServiceOffer',
                    index: i,
                });
            }
        }
    }

    async removeInclusiveListServiceOffer(index) {
        if (this.filter?.inclusiveListServiceOffer[index]) {
            this.filter?.inclusiveListServiceOffer.splice(index, 1);
            this.hotelService.inclusiveListServiceOffer(this.filter?.inclusiveListServiceOffer);
        }
    }

    checkCategory(): void {
        if (this.filter?.minCategory > 0) {
            this.list.push({
                name: this.filter?.minCategory + ' Sterne',
                function: 'removeCategory'
            });
        }
    }

    async removeCategory() {
        this.hotelService.category(0, this.mode !== 'home');
    }

    checkRecommendation(): void {
        if (this.filter?.recommendation > 0) {
            this.list.push({
                name: 'min ' + this.filter?.recommendation + '%',
                function: 'removeRecommendation'
            });
        }
    }

    async removeRecommendation() {
        this.hotelService.recommendation(null, this.mode !== 'home');
    }

    checkRecommendationCount(): void {
        if (this.filter?.recommendationcount > 0) {
            this.list.push({
                name: 'Anzahl Bewertungen ' + this.filter?.recommendationcount,
                function: 'removeRecommendationCount'
            });
        }
    }

    async removeRecommendationCount() {
        this.hotelService.recommendationcount(null, this.mode !== 'home');
    }

    checkMeerblibk(): void {
        if (this.filter?.zommermitmeerblick) {
            this.list.push({
                name: 'Meerblick',
                function: 'removeMeerblibk'
            });
        }
    }

    async removeMeerblibk() {
        this.hotelService.zommermitmeerblick(null, this.mode !== 'home');
    }

    checkMeerseite(): void {
        if (this.filter?.meerseite) {
            this.list.push({
                name: 'Meerseite',
                function: 'removeMeerseite'
            });
        }
    }

    async removeMeerseite() {
        this.hotelService.meerseite(null, this.mode !== 'home');
    }

    checkboardTypeList(): void {
        if (this.filter?.boardTypeList?.length > 0) {
            for (let i = 0; i < this.filter?.boardTypeList.length; i++) {
                this.list.push({
                    name: this.filter?.boardTypeList[i].name,
                    function: 'removeboardTypeList',
                    index: i
                });
            }
        }
    }

    async removeboardTypeList(index) {
        if (this.filter?.boardTypeList[index]) {
            this.hotelService.boardTypeList(this.filter?.boardTypeList[index], false, this.mode !== 'home');
        }
    }

    checklocationCode(): void {
        if (this.filter?.locatedList?.length > 0) {
            for (let i = 0; i < this.filter?.locatedList.length; i++) {
                this.list.push({
                    name: this.filter?.locatedList[i].name,
                    function: 'removelocationCode',
                    index: i
                });
            }
        }
    }

    async removelocationCode(index) {
        if (this.filter?.locatedList[index]) {
            this.hotelService.locatedList(this.filter?.locatedList[index], false, this.mode !== 'home');
        }
    }

    checktransferlist(): void {
        if (this.filter?.transferlist?.length > 0) {
            for (let i = 0; i < this.filter?.transferlist.length; i++) {
                if (this.filter?.transferlist[i] === 'MT' || this.filter?.transferlist[i].code === 'MT') {
                    this.list.push({
                        name: 'mit Transfer',
                        function: 'removetransferlist',
                        index: i
                    });
                } else if (this.filter?.transferlist[i] === 'OT' || this.filter?.transferlist[i].code === 'OT') {
                    this.list.push({
                        name: 'ohne Transfer',
                        function: 'removetransferlist',
                        index: i
                    });
                } else if (this.filter?.transferlist[i] === 'MW' || this.filter?.transferlist[i].code === 'MW') {
                    this.list.push({
                        name: 'Mietwagen',
                        function: 'removetransferlist',
                        index: i
                    });
                } else if (this.filter?.transferlist[i] === 'ZG' || this.filter?.transferlist[i].code === 'ZG') {
                    this.list.push({
                        name: 'Zug zum Flug',
                        function: 'removetransferlist',
                        index: i
                    });
                }
            }
        }
    }

    async removetransferlist(index) {
        if (this.filter?.transferlist[index]) {
            this.hotelService.transferlist(this.filter?.transferlist[index], false, this.mode !== 'home');
        }
    }

    checkdirektflug(): void {
        if (this.filter?.flight?.directness === 'N') {
            this.list.push({
                name: 'Direktflug',
                function: 'removedirektflug'
            });
        } else if (this.filter?.flight?.stopover === '1') {
            this.list.push({
                name: 'max 1 Stopp',
                function: 'removestopover'
            });
        } else if (this.filter?.flight?.stopover === '2') {
            this.list.push({
                name: 'max 1 Stopp',
                function: 'removestopover'
            });
        }
    }

    async removestopover() {
        this.hotelService.stopover(null, this.mode !== 'home');
    }

    async removedirektflug() {
        this.hotelService.directness(null, this.mode !== 'home');
    }

    checktarifclass(): void {
        if (this.filter?.flight?.tariff === 'SPAR') {
            this.list.push({
                name: 'Sparpreis',
                function: 'removetarifclass'
            });
        }
        if (this.filter?.flight?.tariff === 'FLEX') {
            this.list.push({
                name: 'Flexpreis',
                function: 'removetarifclass'
            });
        }
    }

    async removetarifclass() {
        this.hotelService.tarifclass(null, this.mode !== 'home');
    }

    checkbookingclass(): void {
        if (this.filter?.flight?.bookingClass === '1') {
            this.list.push({
                name: '1. Klasse',
                function: 'removetarifclass'
            });
        }
        if (this.filter?.flight?.bookingClass === '2') {
            this.list.push({
                name: '2. Klasse',
                function: 'removebookingclass'
            });
        }
    }

    async removebookingclass() {
        this.hotelService.bookingClass(null, true);
    }

    checkTravelClass(): void {
        if (this.filter?.flight?.travelClass === 'economy') {
            this.list.push({
                name: 'Flugklasse: Economy',
                function: 'removeTravelClass'
            });
        } else if (this.filter?.flight?.travelClass === 'premium') {
            this.list.push({
                name: 'Flugklasse: Premium Eco',
                function: 'removeTravelClass'
            });
        } else if (this.filter?.flight?.travelClass === 'business') {
            this.list.push({
                name: 'Flugklasse: Business',
                function: 'removeTravelClass'
            });
        } else if (this.filter?.flight?.travelClass === 'first') {
            this.list.push({
                name: 'Flugklasse: First',
                function: 'removeTravelClass'
            });
        }
    }

    async removeTravelClass() {
        this.hotelService.travelClass(null, this.mode !== 'home');
    }

    checkinclusiveList(): void {
        if (this.filter?.inclusiveList?.length > 0) {
            for (let i = 0; i < this.filter?.inclusiveList.length; i++) {
                this.list.push({
                    name: this.filter?.inclusiveList[i].name,
                    function: 'removeinclusiveList',
                    index: i
                });
            }
        }
    }

    async removeinclusiveList(index) {
        if (this.filter?.inclusiveList[index]) {
            this.hotelService.inclusiveList(this.filter?.inclusiveList[index], false, this.mode !== 'home');
        }
    }

    checkFacilityList(): void {
        if (this.filter?.facilityList?.length > 0) {
            for (let i = 0; i < this.filter?.facilityList.length; i++) {
                this.list.push({
                    name: this.filter?.facilityList[i].name,
                    function: 'removeFacilityList',
                    index: i
                });
            }
        }
    }

    async removeFacilityList(index) {
        if (this.filter?.facilityList[index]) {
            this.hotelService.facilityList(this.filter?.facilityList[index], false, this.mode !== 'home');
        }
    }

    checkroomTypeList(): void {
        if (this.filter?.roomTypeList?.length > 0) {
            for (let i = 0; i < this.filter?.roomTypeList.length; i++) {
                this.list.push({
                    name: this.filter?.roomTypeList[i].name,
                    function: 'removeroomTypeList',
                    index: i
                });
            }
        }
    }

    async removeroomTypeList(index) {
        if (this.filter?.roomTypeList[index]) {
            this.hotelService.roomTypeList(this.filter?.roomTypeList[index], false);
        }
    }

    checkroomIdList(): void {
        if (this.filter?.roomIdList?.length > 0) {
            for (let i = 0; i < this.filter?.roomIdList.length; i++) {
                this.list.push({
                    name: this.filter?.roomIdList[i].name + ' (' + this.filter?.roomIdList[i].code + ')',
                    function: 'removeroomIdList',
                    index: i
                });
            }
        }
    }

    async removeroomIdList(index) {
        if (this.filter?.roomIdList[index]) {
            this.hotelService.roomIdList(this.filter?.roomIdList[index], false);
        }
    }

    checkserviceCodeList(): void {
        if (this.filter?.serviceCode?.length > 0) {
            for (let i = 0; i < this.filter?.serviceCode.length; i++) {
                this.list.push({
                    name: this.filter?.serviceCode[i].name + ' (' + this.filter?.serviceCode[i].code + ')',
                    function: 'removeserviceCodeList',
                    index: i
                });
            }
        }
    }

    async removeserviceCodeList(index) {
        if (this.filter?.serviceCode[index]) {
            this.hotelService.serviceCode(this.filter?.serviceCode[index], false);
        }
    }

    checktourOperatorList(): void {
        if (this.filter?.tourOperatorList?.length > 0) {
            for (let i = 0; i < this.filter?.tourOperatorList.length; i++) {
                this.list.push({
                    name: this.filter?.tourOperatorList[i].code,
                    function: 'removetourOperatorList',
                    index: i
                });
            }
        }
    }

    async removetourOperatorList(index) {
        if (this.filter?.tourOperatorList[index]) {
            this.hotelService.tourOperatorList(this.filter?.tourOperatorList[index], false);
        }
    }

    checkkeywordList(): void {
        if (this.filter?.keywordList?.length > 0) {
            for (let i = 0; i < this.filter?.keywordList.length; i++) {
                this.list.push({
                    name: this.filter?.keywordList[i].name,
                    function: 'removekeywordList',
                    index: i
                });
            }
        }
    }

    async removekeywordList(index) {
        if (this.filter?.keywordList[index]) {
            this.hotelService.keywordList(this.filter?.keywordList[index], false);
        }
    }

    checkarrivalAirportList(): void {
        if (this.filter?.arrivalAirportList?.length > 0) {
            for (let i = 0; i < this.filter?.arrivalAirportList.length; i++) {
                this.list.push({
                    name: 'Ziel ' + this.filter?.arrivalAirportList[i].name + ' ' + this.filter?.arrivalAirportList[i].code,
                    function: 'removearrivalAirportList',
                    index: i
                });
            }
        }
    }

    async removearrivalAirportList(index) {
        if (this.filter?.arrivalAirportList[index]) {
            this.filter?.arrivalAirportList.splice(index, 1);
            this.hotelService.arrivalAirportList(this.filter?.arrivalAirportList, this.mode !== 'home');
        }
    }

    checkdeparturAirportList(): void {
        if (this.filter?.airportsResult?.length > 0) {
            for (let i = 0; i < this.filter?.airportsResult.length; i++) {
                this.list.push({
                    name: 'Ab ' + this.filter?.airportsResult[i].name + ' ' + this.filter?.airportsResult[i].code,
                    function: 'removedeparturAirportList',
                    index: i
                });
            }
        }
    }

    async removedeparturAirportList(index) {
        if (this.filter?.airportsResult[index]) {
            this.filter?.airportsResult.splice(index, 1);
            this.hotelService.departurAirportList(this.filter?.airportsResult, this.mode !== 'home');
        }
    }

    checkhotelChainList(): void {
        if (this.filter?.hotelChainList?.length > 0) {
            for (let i = 0; i < this.filter?.hotelChainList.length; i++) {
                this.list.push({
                    name: this.filter?.hotelChainList[i].name,
                    function: 'removehotelChainList',
                    index: i
                });
            }
        }
    }

    async removehotelChainList(index) {
        if (this.filter?.hotelChainList[index]) {
            this.filter?.hotelChainList.splice(index, 1);
            this.hotelService.hotelChainList(this.filter?.hotelChainList);
        }
    }

    checkflightCarrierList(): void {
        if (this.filter?.flightCarrierList?.length > 0) {
            for (let i = 0; i < this.filter?.flightCarrierList.length; i++) {
                this.list.push({
                    name: this.filter?.flightCarrierList[i].name,
                    function: 'removeflightCarrierList',
                    index: i
                });
            }
        }
    }

    async removeflightCarrierList(index) {
        if (this.filter?.flightCarrierList[index]) {
            this.filter?.flightCarrierList.splice(index, 1);
            this.hotelService.flightCarrierList(this.filter?.flightCarrierList);
        }
    }

    checkdepartureTime(): void {
        let text = '';
        if (this.filter?.mindepartureTime > 0 && this.filter?.mindepartureTime < 1440) {
            text = this.transformMinute(this.filter?.mindepartureTime);
        }
        if (this.filter?.maxdepartureTime > 0 && this.filter?.maxdepartureTime < 1440) {
            if (text?.length > 0) {
                text += '-' + this.transformMinute(this.filter?.maxdepartureTime);
            } else {
                text = 'vor ' + this.transformMinute(this.filter?.maxdepartureTime);
            }
        } else if (text?.length > 0) {
            text = 'nach ' + text;
        }
        if (text?.length > 0) {
            this.list.push({
                name: 'Hinflugzeit ' + text,
                function: 'removedepartureTime'
            });
        }
    }

    transformMinute(value: number): string {
        const hours = ('00' + Math.floor(value / 60)).slice(-2);
        const minutes = ('00' + Math.floor(value % 60)).slice(-2);
        return hours + ':' + minutes;
    }

    async removedepartureTime() {
        this.hotelService.departureTime(null, true);
    }

    checkreturnTime(): void {
        let text = '';
        if (this.filter?.minreturnTime > 0 && this.filter?.minreturnTime < 1440) {
            text = this.transformMinute(this.filter?.minreturnTime);
        }
        if (this.filter?.maxreturnTime > 0 && this.filter?.maxreturnTime < 1440) {
            if (text?.length > 0) {
                text += '-' + this.transformMinute(this.filter?.maxreturnTime);
            } else {
                text = 'vor ' + this.transformMinute(this.filter?.maxreturnTime);
            }
        } else if (text?.length > 0) {
            text = 'nach ' + text;
        }
        if (text?.length > 0) {
            this.list.push({
                name: 'Rückflugzeit ' + text,
                function: 'removereturnTime'
            });
        }
    }

    async removereturnTime() {
        this.hotelService.returnTime(null, this.mode !== 'home');
    }

    async resetAll() {
        this.hotelService.resetAllExtras();
    }
}
